<template>
  <div
    class="chat-box"
    :class="{
      'chat-box--inverse': inverse,
      'chat-box--small': small
    }"
    v-on="hoverHandlers"
  >
    <input
      ref="input"
      v-model="message"
      type="text"
      enterkeyhint="Send"
      class="chat-box__textarea"
      :class="{
        'chat-box__textarea--inverse': inverse,
        'chat-box__textarea--full': isFocused && small
      }"
      placeholder="Send message"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="closeEmojis"
      @keydown.enter="sendMessage()"
    >
    <div
      v-if="emojis"
      class="chat-box__emoji"
    >
      <base-icon
        v-if="canShowEmojisIcon"
        icon="emoji"
        @click="toggleEmojis"
      />
    </div>
    <div
      v-if="showEmojis"
      class="chat-box__emojis"
    >
      <v-emoji-picker
        v-click-outside="closeEmojis"
        :emoji-size="27"
        :emoji-with-border="false"
        :emojis-by-row="10"
        @select="onSelectEmoji"
      />
      <div class="arrow-down" />
    </div>
  </div>
</template>

<script>
import { VEmojiPicker } from 'v-emoji-picker';
import WhichBrowser from '@/components/mixins/whichbrowser';
import Hoverable from '@/components/mixins/hoverable';

export default {
  name: 'ChatBox',
  components: {
    VEmojiPicker,
  },
  mixins: [WhichBrowser, Hoverable],
  props: {
    inverse: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    emojis: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isFocused: false,
      message: '',
      showEmojis: false, // This shows the emojis popup
    };
  },
  computed: {
    canShowEmojisIcon() {
      // Emojis will be available if the message is not empty
      if (this.message !== '') {
        return true;
      }

      // Emojis will be available if the message is we are on iPad (We dont have hover)
      if (this.isIPad) {
        return true;
      }

      // Hover - This comes from hoverable.js mixin
      if (this.isHover) {
        return true;
      }

      // By default if we have focus over the input
      return this.isFocused;
    },
  },
  mounted() {
    this.$nextTick().then(() => {
      this.$refs.input.focus();
    });
  },
  methods: {
    onFocus() {
      this.isFocused = true;
    },
    onBlur() {
      this.isFocused = false;
    },
    toggleEmojis() {
      if (this.showEmojis === true) {
        this.closeEmojis();
        return;
      }
      // We have this small delay to prevent from closing due v-click-outside event
      setTimeout(() => {
        this.showEmojis = true;
      }, 100);
    },
    closeEmojis() {
      this.showEmojis = false;
    },
    onSelectEmoji(evt) {
      this.message += `${evt.data} `;

      /** Emojis box doesn't close after selecting an emoji
       * More info: https://helloseer.atlassian.net/browse/SEER-11?focusedCommentId=10126
      */
      // this.closeEmojis();

      // Back to focus after selecting an emoji
      this.$nextTick().then(() => {
        this.$refs.input.focus();
      });
    },
    sendMessage() {
      if (!this.message) {
        return;
      }
      this.$emit('message', this.message);
      this.$nextTick().then(() => {
        this.message = '';

        // Chat box disappear after message is sent (on mobile) https://helloseer.atlassian.net/browse/SEER-241
        if (this.small) {
          this.$refs.input.blur();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-box{
    position: relative;
    width: 380px;

    &--small{
      width: 100%;
      // max-width: 380px;
    }

    &__emoji{
        position: absolute;
        right: 16px;
        top: 12px;
    }

    &__emojis{
      position: absolute;
      bottom: calc(100% + 10px);
      right: 26px;
      transform: translateX(50%);

      .arrow-down {
        position: absolute;
        right: 50%;
        transform: translateX(50%) translateY(-1px);
        width: 0;
        height: 0;

        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid #f0f0f0;
      }

      &::v-deep .container-emoji{
        height: 105px;
      }
    }

    &__textarea {
        border: solid 1px $black;
        font-family: $font-family-sans-serif;
        font-size: 16px;
        line-height: 19px;
        background: none;
        resize: none;
        height: 48px;
        padding: 16px 12px;
        padding-right: 46px;
        border-radius: 8px;
        width: 100%;
        max-width: 380px;

        &--inverse{
          border: solid 1px white;
          color: white;
          &::placeholder{
            color: white;
          }
        }
        &--full{
          max-width: 100%;
        }
        &:focus {
            outline: none;
        }
    }
}
</style>
